import { useCallback, useEffect, useRef, useState } from 'react';
import { previewPromo } from '../api/promoCodesApi';
import { getPromoErrorText } from '../utils/promoErrors';
import { normalizePromoCode } from '../utils/promoCode';

export { normalizePromoCode } from '../utils/promoCode';

export default function useCheckoutPromo({ products, userId, isAdmin, token }) {
  const [text, setTextValue] = useState('');
  const [appliedCode, setAppliedCode] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const sequence = useRef(0);
  const signature = JSON.stringify([userId, isAdmin, token, products.map(p => [p.id, p.quantity, p.price, p.discountedPrice, p.toOrder])]);
  const context = useRef();
  context.current = { products, userId, isAdmin, signature, code: normalizePromoCode(text) };

  const validate = useCallback(async code => {
    const current = context.current;
    const id = ++sequence.current;
    if (current.code !== code) { setBusy(false); setResult(null); return null; }
    setBusy(true); setError(''); setResult(null);
    try {
      const quote = await previewPromo({
        promoCode: code,
        userId: current.userId,
        products: current.products,
      }, current.isAdmin);
      if (!quote || !Number.isInteger(quote.finalTotal) || !Number.isInteger(quote.promoDiscountAmount)
          || !Array.isArray(quote.items)) throw new Error('Не вдалося отримати розрахунок промокоду');
      if (id === sequence.current && context.current.signature === current.signature && context.current.code === code) {
        setResult({ signature: current.signature, code, quote });
      }
      return quote;
    } catch (reason) {
      if (id === sequence.current && context.current.code === code) setError(getPromoErrorText(reason));
      return null;
    } finally { if (id === sequence.current) setBusy(false); }
  }, []);

  // A quote belongs to one exact basket and customer. Ignore stale responses.
  useEffect(() => {
    if (appliedCode) validate(appliedCode);
    return () => { sequence.current += 1; };
  }, [signature, appliedCode, validate]);

  const setText = value => { setTextValue(value); setError(''); };

  const apply = () => {
    const code = normalizePromoCode(text);
    if (!code) { setError('Введіть промокод'); return; }
    setResult(null);
    if (code === appliedCode) validate(code);
    else { setText(text.trim()); setAppliedCode(code); }
  };
  const remove = () => {
    sequence.current += 1; setAppliedCode(''); setText(''); setResult(null); setError(''); setBusy(false);
  };
  const quote = result?.signature === signature && result?.code === appliedCode && result?.code === normalizePromoCode(text) ? result.quote : null;
  return { text, setText, appliedCode, quote, error, busy, apply, remove, quotedUserId: quote ? userId : undefined,
    blocked: Boolean(appliedCode) && !quote };
}
